import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Trash2, ArrowRight, ShoppingBag, Sparkles, Ticket, X, Info } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useMarketCart } from '../hooks/useMarketCart';
import { MarketDrawerLines } from './market/MarketDrawerLines';
import { formatCurrency, formatApiErrorDetail } from '../lib/utils';

/**
 * CartIcon — header + mobile-nav cart trigger with a right-side drawer.
 *
 * Aug 2026 overhaul (Msg 205):
 *   • Shows the REAL per-item price computed by the backend
 *     (/checkout/cart/quote). Membership tier is resolved server-side so
 *     the number matches what /checkout will actually charge.
 *   • Order Summary block with Subtotal · Promo Discount · Final Total.
 *   • Promo Code input with Apply / Remove. Backend re-validates the code
 *     at checkout so a spoofed frontend can never sneak a discount through.
 */
const CartIcon = ({ variant = 'header', className = '' }) => {
  const { cart, itemCount: classCount, removeItem, clear, drawerOpen, openDrawer, closeDrawer } = useCart();
  const market = useMarketCart();
  // One badge for everything in the basket: class/package lines + Runners' Market lines.
  const itemCount = classCount + market.count;
  const open = drawerOpen;
  const setOpen = (next) => (next ? openDrawer() : closeDrawer());

  const trigger = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={itemCount ? `Cart · ${itemCount} item${itemCount === 1 ? '' : 's'}` : 'Cart'}
      className={
        variant === 'mobile'
          ? `relative inline-flex flex-col items-center justify-center text-[#3F2774] px-2 py-1 gap-0.5 ${className}`
          : `relative h-9 w-9 sm:h-10 sm:w-10 inline-flex items-center justify-center rounded-full border border-slate-200 bg-white text-[#3F2774] hover:border-[#A855F7] hover:bg-[#F5F3FF] transition-colors ${className}`
      }
      data-testid="cart-icon"
    >
      <ShoppingCart size={variant === 'mobile' ? 20 : 16} />
      {variant === 'mobile' && (
        <span className="text-[10px] font-black uppercase tracking-widest">Cart</span>
      )}
      {itemCount > 0 && (
        <span
          className={`absolute ${variant === 'mobile' ? 'top-0 right-2' : '-top-1 -right-1'} min-w-[18px] h-[18px] px-1 rounded-full bg-[#7E22CE] text-white text-[10px] font-black flex items-center justify-center`}
          data-testid="cart-icon-badge"
        >
          {itemCount > 9 ? '9+' : itemCount}
        </span>
      )}
    </button>
  );

  return (
    <>
      {trigger}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
            <SheetTitle className="flex items-center gap-2 text-[#3F2774]">
              <ShoppingBag size={18} className="text-[#7E22CE]" />
              Your Cart {itemCount > 0 && <span className="text-slate-500 text-sm">({itemCount})</span>}
            </SheetTitle>
            <p className="text-[11px] text-slate-500 mt-1">
              Availability + membership pricing are re-checked at every step. Nothing is booked or charged yet.
            </p>
          </SheetHeader>

          {itemCount === 0 ? (
            <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="cart-drawer-list">
              <EmptyCart onClose={() => setOpen(false)} />
            </div>
          ) : classCount === 0 ? (
            <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="cart-drawer-list">
              <MarketDrawerLines market={market} onClose={() => setOpen(false)} />
            </div>
          ) : (
            <CartDrawerBody
              cart={cart}
              open={open}
              onClose={() => setOpen(false)}
              onRemoveLine={removeItem}
              onClearAll={clear}
              topSlot={<MarketDrawerLines market={market} onClose={() => setOpen(false)} />}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

const EmptyCart = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <div className="text-center py-16" data-testid="cart-empty">
      <div className="mx-auto w-14 h-14 rounded-full bg-[#F5F3FF] text-[#7E22CE] flex items-center justify-center mb-3">
        <ShoppingBag size={22} />
      </div>
      <p className="text-sm font-black text-[#3F2774]">Your cart is empty</p>
      <p className="text-xs text-slate-500 mt-1 max-w-[28ch] mx-auto">
        Browse classes, packages or the Runners' Market, then add them here before heading to checkout.
      </p>
      <div className="flex flex-wrap gap-2 justify-center mt-4">
        <Button
          variant="outline"
          className="text-xs font-black uppercase tracking-widest"
          onClick={() => { onClose(); navigate('/all-classes'); }}
          data-testid="cart-empty-browse-classes"
        >
          Browse Classes
        </Button>
        <Button
          className="text-xs font-black uppercase tracking-widest bg-[#5530B5] hover:bg-[#3F2774]"
          onClick={() => { onClose(); navigate('/private-coaching-packages'); }}
          data-testid="cart-empty-browse-packages"
        >
          Explore Packages
        </Button>
        <Button
          variant="outline"
          className="text-xs font-black uppercase tracking-widest border-[#7E22CE] text-[#7E22CE] hover:bg-[#F5F3FF]"
          onClick={() => { onClose(); navigate('/runner-market'); }}
          data-testid="cart-empty-browse-market"
        >
          Runners' Market
        </Button>
      </div>
    </div>
  );
};

// ── Drawer body ──────────────────────────────────────────────────────────
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'lgra.cart.session_id';
const buildHeaders = () => {
  const headers = {};
  try {
    const sid = window.localStorage.getItem(SESSION_KEY);
    if (sid) headers['X-Cart-Session'] = sid;
  } catch (_) { /* silent */ }
  return headers;
};

const CartDrawerBody = ({ cart, open, onClose, onRemoveLine, onClearAll, topSlot = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh } = useCart();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [applying, setApplying] = useState(false);

  const loadQuote = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/checkout/cart/quote`, {},
        { headers: buildHeaders(), withCredentials: true },
      );
      setQuote(data);
      setPromoInput('');
      setPromoError('');
    } catch (_) {
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  // Load quote every time the drawer opens or the cart items count changes.
  useEffect(() => {
    if (!open) return;
    loadQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, (cart?.items || []).length]);

  const remove = async (itemKey) => {
    await onRemoveLine(itemKey);
    await loadQuote();
  };

  const applyPromo = async () => {
    const code = (promoInput || '').trim().toUpperCase();
    if (!code) { setPromoError('Please enter a promo code.'); return; }
    setApplying(true);
    setPromoError('');
    try {
      await axios.post(
        `${API}/cart/promo-code`, { code },
        { headers: buildHeaders(), withCredentials: true },
      );
      await refresh();
      await loadQuote();
    } catch (err) {
      setPromoError(formatApiErrorDetail(err.response?.data?.detail) || err.message || 'Could not apply promo code.');
    } finally {
      setApplying(false);
    }
  };

  const removePromo = async () => {
    setApplying(true);
    try {
      await axios.delete(`${API}/cart/promo-code`, {
        headers: buildHeaders(), withCredentials: true,
      });
      await refresh();
      await loadQuote();
    } catch (_) { /* silent */ }
    setApplying(false);
  };

  const items = quote?.items || cart.items || [];
  const subtotal = quote?.subtotal_cents ?? 0;
  const discount = quote?.discount_cents ?? 0;
  const finalTotal = quote?.final_total_cents ?? quote?.total_cents ?? 0;
  const promoStatus = quote?.promo_status;
  const appliedCode = quote?.promo_code;
  const tierLabel = ({
    general: 'Public', lgra: 'LGRA Active', gold: 'Gold', platinum: 'Platinum',
  })[user?.membership_tier || user?.tier || 'general'] || 'Public';

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="cart-drawer-list">
        {topSlot}
        {topSlot && <h3 className="text-[10px] font-black uppercase tracking-widest text-[#3F2774] mb-2" data-testid="cart-drawer-classes-heading">Classes & packages</h3>}
        {user && (
          <div className="mb-3 flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
            <span className="text-slate-500">Pricing at your</span>
            <span className="inline-flex items-center gap-1 text-[#7E22CE] bg-[#F5F3FF] border border-[#E9D5FF] rounded-full px-2 py-0.5" data-testid="cart-drawer-tier-chip">
              <Sparkles size={10} /> {tierLabel} rate
            </span>
          </div>
        )}
        {!user && (
          <div className="mb-3 flex items-start gap-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-2" data-testid="cart-drawer-guest-hint">
            <Info size={12} className="text-slate-400 mt-0.5 shrink-0" />
            <span>Guest pricing (Public). Sign in on checkout to unlock member rates.</span>
          </div>
        )}
        <ul className="space-y-3">
          {items.map((q) => (
            <CartRow
              key={q.item_key}
              quote={q}
              loading={loading && !quote}
              onRemove={() => remove(q.item_key)}
            />
          ))}
        </ul>
      </div>

      <div className="border-t border-slate-100 px-5 py-4 space-y-3" data-testid="cart-drawer-footer">
        {/* Promo Code */}
        <div className="rounded-lg border border-slate-200 bg-[#FAFAFA] p-3" data-testid="cart-drawer-promo">
          {appliedCode && promoStatus?.valid ? (
            <div className="flex items-start gap-2" data-testid="cart-drawer-promo-applied">
              <Ticket size={14} className="text-emerald-700 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black text-emerald-800">
                  {appliedCode} applied · {formatCurrency(discount)} off
                </div>
                <p className="text-[10px] text-emerald-700 mt-0.5">
                  {promoStatus.message}
                </p>
              </div>
              <button
                type="button"
                onClick={removePromo}
                disabled={applying}
                className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-red-600 disabled:opacity-50"
                data-testid="cart-drawer-promo-remove"
              >
                Remove
              </button>
            </div>
          ) : (
            <>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                Promo Code
              </label>
              <div className="flex gap-2">
                <Input
                  value={promoInput}
                  onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                  placeholder="Enter promo code"
                  className="h-9 text-sm"
                  data-testid="cart-drawer-promo-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }}
                />
                <Button
                  type="button"
                  onClick={applyPromo}
                  disabled={applying || !promoInput.trim()}
                  className="h-9 bg-[#5530B5] hover:bg-[#3F2774] text-xs font-black uppercase tracking-widest px-4"
                  data-testid="cart-drawer-promo-apply"
                >
                  Apply
                </Button>
              </div>
              {promoError && (
                <p className="text-[10px] text-red-700 mt-1" data-testid="cart-drawer-promo-error">{promoError}</p>
              )}
              {!promoError && appliedCode && promoStatus && !promoStatus.valid && (
                <p className="text-[10px] text-amber-700 mt-1" data-testid="cart-drawer-promo-inactive">
                  {promoStatus.message}
                </p>
              )}
            </>
          )}
        </div>

        {/* Order Summary */}
        <div className="rounded-lg bg-white border border-slate-200 p-3 space-y-1.5" data-testid="cart-drawer-summary">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-600">Subtotal</span>
            <span className="font-black text-[#101B3A]" data-testid="cart-drawer-subtotal">
              {loading && !quote ? '…' : formatCurrency(subtotal)}
            </span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-emerald-800">Promo discount ({appliedCode})</span>
              <span className="font-black text-emerald-800" data-testid="cart-drawer-discount">
                − {formatCurrency(discount)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm border-t border-slate-100 pt-1.5">
            <span className="font-black text-[#101B3A]">Final total</span>
            <span className="text-lg font-black text-[#7E22CE]" data-testid="cart-drawer-total">
              {loading && !quote ? '…' : formatCurrency(finalTotal)}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onClearAll}
            className="text-xs font-black uppercase tracking-widest"
            data-testid="cart-footer-clear"
          >
            <Trash2 size={12} className="mr-1" /> Clear
          </Button>
          <Button
            className="flex-1 bg-[#5530B5] hover:bg-[#3F2774] text-xs font-black uppercase tracking-widest"
            onClick={() => { onClose(); navigate('/checkout'); }}
            data-testid="cart-footer-checkout"
          >
            Continue to Checkout <ArrowRight size={12} className="ml-1" />
          </Button>
        </div>
      </div>
    </>
  );
};

// ── Cart row (verified per-item price) ───────────────────────────────────
const CartRow = ({ quote, loading, onRemove }) => {
  const d = quote?.display || {};
  const isPrivate = quote?.kind === 'private_booking';
  const listCents = Number(d.list_price_cents || 0);
  const priceCents = Number(quote?.price_cents ?? 0);
  const showStrike = !isPrivate && listCents > 0 && listCents > priceCents;
  const title = isPrivate
    ? `Private Class · ${d.coach_name || 'Coach'}`
    : (d.name || d.title || quote?.kind);
  const sessions = d.class_count ? `${d.class_count} sessions` : (d.hours_count ? `${d.hours_count} hours` : (d.credits_amount ? `${d.credits_amount} credits` : ''));
  const subtitle = isPrivate
    ? `${d.date || ''} · ${d.start_time || ''} · ${d.duration_hours || 1}h`
    : quote?.kind === 'package'
      ? `${sessions}${sessions ? ' · ' : ''}${d.package_type || ''}`
      : `${d.date || ''} ${d.start_time || d.time || ''} · ${d.location || ''}`;
  const invalid = quote && quote.valid === false;

  return (
    <li className={`rounded-lg border ${invalid ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-white'} p-3`} data-testid={`cart-row-${quote?.item_key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black text-[#101B3A] leading-snug truncate" data-testid={`cart-row-name-${quote?.item_key}`}>{title}</p>
          <p className="text-[11px] text-slate-500 mt-0.5 truncate">{subtitle}</p>
          {quote?.attendee_name && (
            <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#5530B5] bg-[#F5F3FF] border border-[#D8C4FF] rounded-full px-2 py-0.5">
              For {quote.attendee_name}
            </span>
          )}
          {invalid && (
            <p className="mt-2 text-[11px] text-red-700 font-black" data-testid={`cart-row-error-${quote?.item_key}`}>
              {(quote?.errors || [])[0]?.message || 'Please review this item at checkout.'}
            </p>
          )}
        </div>
        <div className="text-right shrink-0 min-w-[86px]">
          {loading ? (
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Loading…</span>
          ) : invalid ? (
            <span className="text-[10px] uppercase tracking-widest text-red-600 font-black">Needs review</span>
          ) : quote?.requires_payment === false ? (
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-black" data-testid={`cart-row-price-${quote?.item_key}`}>
              Uses package
            </span>
          ) : (
            <>
              {showStrike && (
                <div className="text-[10px] text-slate-400 line-through" data-testid={`cart-row-list-${quote?.item_key}`}>
                  {formatCurrency(listCents)}
                </div>
              )}
              <div className="text-sm font-black text-[#3F2774]" data-testid={`cart-row-price-${quote?.item_key}`}>
                {formatCurrency(priceCents)}
              </div>
              {Number(quote?.discount_cents || 0) > 0 && (
                <div className="text-[10px] text-emerald-700 font-black" data-testid={`cart-row-discount-${quote?.item_key}`}>
                  − {formatCurrency(quote.discount_cents)} promo
                </div>
              )}
            </>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="block mt-2 ml-auto text-slate-400 hover:text-red-600"
            aria-label="Remove"
            data-testid={`cart-row-remove-${quote?.item_key}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </li>
  );
};

export default CartIcon;
